import React, { PureComponent, createRef } from "react";
import "./PieChart.scss";
import { rawDataQuestion } from "../../global-interfaces";
import {
  select,
  pie,
  arc,
  format,
  range,
  interpolateNumber,
  interpolate,
  scaleOrdinal,
} from "d3";
import { checkIfMobile, checkBrowser } from "../../utils";
import Classnames from "classnames";
import styleVars from "../../styling/variables.scss";
const pct_format: any = format(".0%");

interface ChartProps {
  chart: rawDataQuestion;
  color: string | null;
}
interface State {
  width: number;
  hoveredSlice: number | null;
}

interface LabelProps {
  chartWidth: number;
  showLabel: boolean;
  centroid: Array<number>;
  value: string;
  label: string;
}

class Label extends PureComponent<LabelProps> {
  render() {
    const { showLabel, centroid, label, value } = this.props;
    const side = centroid[0] < 0 ? "left" : "right";

    // create an array of all the words in the labels to only break on spaces
    const labelWords = label.replace("/", "/ ").split(" ");
    // reduce the array into rows by checking if each word would fit in the previous row
    const maxCharacters = 20; // arbitraty max determinned through trial and error
    const labelRows: Array<string> = labelWords.reduce(
      (t: Array<string>, v: string, i: number) => {
        const string = t[t.length - 1] + (i === 0 ? "" : " ") + v;
        return string.length < maxCharacters
          ? [...t.slice(0, t.length - 1), string]
          : t.concat(v);
      },
      [""]
    );
    // 5.8 is the ratio of characters per pixel with this font size
    let width =
      Math.max(
        ...labelRows.map((d: string) => {
          let letterLength = d.length;
          // m/M/w/W are just a lot longer and were screwing up the ratio in font size for width
          if (d.indexOf("m") >= 0 || d.indexOf("M") >= 0) {
            letterLength = letterLength + 1;
          }
          if (d.indexOf("w") >= 0 || d.indexOf("W") >= 0) {
            letterLength = letterLength + 1;
          }
          return letterLength;
        }),
        5
      ) * 5.8;
    // height is dynamic by row, adding 15 pixels for every row. The data point has its own row, so we add one to labelRows.
    let height = (labelRows.length + 1) * 15;

    return (
      <g
        className={Classnames("label-group", { show: showLabel }, side)}
        // this transform function moves the origin point for the group to (nearly) the end of the circle
        transform={`translate(${centroid.map((d: number, i: number) => {
          const distToCircleEdge = d * 2;
          const distToSVGEdge = this.props.chartWidth / 2;
          // the centroid is [x, y] -- so we check for two things:
          // (1) if it is the y value, translate the rect towards the end of the circle but down half the height (so the rect intersects halfway through)
          // (2) if it is the x value, translate the rect to the edge of the circle UNLESS it bumps into the SVG edge, in which case, bump the edge and don't go over
          return i === 1
            ? distToCircleEdge - height / 2
            : Math.abs(distToCircleEdge) + width > distToSVGEdge
            ? (distToSVGEdge - width) *
              // maintain the direction (sign -- positive or negative)
              (distToCircleEdge / Math.abs(distToCircleEdge))
            : distToCircleEdge;
        })})`}
      >
        <rect
          className={Classnames("label-data-rect")}
          width={width}
          height={height}
          transform={side === "left" ? `translate(${-width},0)` : ""}
          rx={0.5}
          filter={
            checkBrowser() !== "Apple Safari" ? "url(#svg-drop-shadow)" : "none"
          }
        />
        <text className={Classnames("label-data-text", side)} dy={"1em"}>
          <tspan className="value" x="0">
            {value}
          </tspan>
          {labelRows.map((d, i) => {
            return (
              <tspan key={i} className="label" x="0" dy="1em">
                {d}
              </tspan>
            );
          })}
        </text>
      </g>
    );
  }
}

class PieChart extends PureComponent<ChartProps, State> {
  private chartDivRef: React.RefObject<HTMLDivElement>;
  private svgRef: React.RefObject<SVGSVGElement>;
  private size: number;
  private colors: Array<string>;
  constructor(props: ChartProps) {
    super(props);
    this.state = {
      width: 0,
      hoveredSlice: null,
    };
    this.svgRef = createRef();
    this.chartDivRef = createRef();
    this.onResize = this.onResize.bind(this);
    this.generatePie = this.generatePie.bind(this);
    this.size = +styleVars.chartHeight.slice(0, -2);
    this.colors = styleVars.secondaryColors.split(", ");
  }
  componentDidMount() {
    this.size =
      (this.chartDivRef.current && this.chartDivRef.current.offsetHeight) ||
      +styleVars.chartHeight.slice(0, -2);
    window.addEventListener("resize", () => {
      this.onResize();
    });
    this.onResize();
  }

  componentWillUnmount() {
    //@ts-ignore
    window.removeEventListener("resize"); // prevent memory leaks
  }

  componentDidUpdate() {
    const { chart } = this.props;
    const { data } = chart;
    const { width } = this.state;
    const radius = Math.min(width, this.size) / 2 - 40;
    const colorScale = scaleOrdinal()
      //@ts-ignore
      .domain(range(0, 7))
      .range(this.colors);

    let arcGen = arc()
      .innerRadius(0)
      .outerRadius(radius);

    const pie = select(this.svgRef.current).select(".pie");
    let slice = pie.selectAll(".pie-path").data(this.generatePie(data));

    let sliceEnter = slice
      .enter()
      .append<SVGPathElement>("path")
      .attr("class", "pie-path")
      .attr("fill", function(d: any) {
        return colorScale(d.index);
      })
      .on("mouseenter", (d: any) => {
        this.setState({ hoveredSlice: d.index });
      });

    // EXIT BEHAVIOR

    slice
      .exit()
      .transition()
      .duration(500)
      //@ts-ignore
      .attrTween("d", function(this: SVGPathElement, d: any) {
        let old_start_angle = d.startAngle;
        let old_end_angle = d.endAngle;
        let pie_top = 2 * Math.PI;
        let i = interpolateNumber(old_end_angle, pie_top);
        let j = interpolateNumber(old_start_angle, pie_top);
        return (
          function(t: number) {
            d.endAngle = i(t);
            d.startAngle = j(t);
            return arcGen(d) || null;
          } || null
        );
      })
      .remove();

    // UPDATE BEHAVIOR

    slice = sliceEnter
      //@ts-ignore
      .merge(slice)
      .transition()
      .duration(500)
      .attrTween("d", function(this: any, d: any) {
        this._current = this._current || d;
        const i = interpolate(this._current, d);
        this._current = i(0);
        return function(t: number) {
          return arcGen(i(t));
        };
      });

    // ENTER BEHAVIOR

    sliceEnter
      .transition()
      .duration(500)
      //@ts-ignore
      .attrTween("d", function(d: any) {
        let new_start_angle = d.startAngle;
        let new_end_angle = d.endAngle;
        let pie_top = 2 * Math.PI;
        let i = interpolateNumber(pie_top, new_start_angle);
        let j = interpolateNumber(pie_top, new_end_angle);
        return function(t: number) {
          d.endAngle = i(t);
          d.startAngle = j(t);
          return arcGen(d);
        };
      });
  }

  onResize() {
    const width =
      (this.chartDivRef.current &&
        this.chartDivRef.current.getBoundingClientRect().width) ||
      0;
    this.setState({
      width,
    });
  }

  generatePie(data: rawDataQuestion["data"]) {
    return (
      pie()
        //@ts-ignore
        .value((d: any) => d.value)(data)
    );
  }

  render() {
    const { chart, color } = this.props;
    const { data } = chart;
    const { width, hoveredSlice } = this.state;
    const radius = Math.min(width, this.size) / 2 - 40;
    const slices = this.generatePie(data);
    const extendData = (slice: any) => ({
      ...slice,
      innerRadius: 0,
      outerRadius: radius,
    });
    const arcGen = arc()
      .innerRadius(0)
      .outerRadius(radius);

    return (
      <div className={Classnames("PieChart", color)} ref={this.chartDivRef}>
        <svg id={"svg"} ref={this.svgRef} height={this.size} width={this.size}>
          <defs>
            <filter
              id="svg-drop-shadow"
              x="-10"
              y="-10"
              width="200"
              height="200"
            >
              <feOffset result="offOut" in="SourceAlpha" dx="2" dy="2" />
              <feColorMatrix
                result="matrixOut"
                in="offOut"
                type="matrix"
                values="
                  0.8 0 0 0 0
                  0 0.8 0 0 0
                  0 0 0.8 0 0
                  0 0 0 0.4 0"
              />
              <feGaussianBlur
                result="blurOut"
                in="matrixOut"
                stdDeviation="5"
              />
              <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
            </filter>
          </defs>
          <g
            className="pie"
            transform={`translate(${width / 2}, ${this.size / 2})`}
          />
          <g
            className="labels"
            transform={`translate(${width / 2}, ${this.size / 2})`}
          >
            {slices.map((d: any, i: number) => (
              <Label
                key={i}
                chartWidth={width}
                showLabel={
                  d.value > (checkIfMobile() ? 0.12 : 0.07) ||
                  slices.length < 3 ||
                  hoveredSlice === d.index
                }
                centroid={arcGen.centroid(extendData(d))}
                value={pct_format(d.value)}
                label={d.data.label}
              />
            ))}
          </g>
        </svg>
      </div>
    );
  }
}

export default PieChart;
